import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Clock, Edit, Trash2, Save, X } from 'lucide-react'
import { api } from '../lib/api'
import { validateCronExpression } from '../lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'

const scheduleSchema = z.object({
  scheduleType: z.enum(['manual', 'interval', 'cron']),
  intervalMinutes: z.number().min(1).optional(),
  cronExpression: z.string().optional(),
  timezone: z.string().optional(),
}).refine((data) => {
  if (data.scheduleType === 'interval') {
    return data.intervalMinutes && data.intervalMinutes > 0
  }
  if (data.scheduleType === 'cron') {
    return data.cronExpression && validateCronExpression(data.cronExpression)
  }
  return true
}, {
  message: 'Please provide valid schedule configuration',
  path: ['scheduleType']
})

type ScheduleFormData = z.infer<typeof scheduleSchema>

interface ScheduleEditorProps {
  testId: number
  currentSchedule?: {
    cron: string
    timezone: string
  }
}

export function ScheduleEditor({ testId, currentSchedule }: ScheduleEditorProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Determine current schedule type and values
  const getScheduleType = (cron?: string): 'manual' | 'interval' | 'cron' => {
    if (!cron) return 'manual'
    
    // Check if it's an interval pattern like */30 * * * *
    const intervalMatch = cron.match(/^\*\/(\d+) \* \* \* \*$/)
    if (intervalMatch) return 'interval'
    
    return 'cron'
  }

  const getIntervalMinutes = (cron?: string): number => {
    if (!cron) return 60
    const intervalMatch = cron.match(/^\*\/(\d+) \* \* \* \*$/)
    return intervalMatch ? parseInt(intervalMatch[1]) : 60
  }

  const form = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      scheduleType: getScheduleType(currentSchedule?.cron),
      intervalMinutes: getIntervalMinutes(currentSchedule?.cron),
      cronExpression: currentSchedule?.cron || '0 9 * * *',
      timezone: currentSchedule?.timezone || 'UTC',
    },
  })

  // Update schedule mutation
  const updateScheduleMutation = useMutation({
    mutationFn: (data: ScheduleFormData) => {
      if (data.scheduleType === 'manual') {
        return api.deleteTestSchedule(testId)
      }
      
      const cron = data.scheduleType === 'cron' 
        ? data.cronExpression!
        : `*/${data.intervalMinutes} * * * *`
      
      return api.updateTestSchedule(testId, {
        cron,
        timezone: data.timezone || 'UTC'
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test', testId] })
      setIsEditing(false)
      toast({
        title: 'Schedule updated',
        description: 'Test schedule has been updated successfully.',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update schedule. Please try again.',
        variant: 'destructive',
      })
    },
  })

  // Delete schedule mutation
  const deleteScheduleMutation = useMutation({
    mutationFn: () => api.deleteTestSchedule(testId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test', testId] })
      toast({
        title: 'Schedule removed',
        description: 'Test schedule has been removed successfully.',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to remove schedule. Please try again.',
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: ScheduleFormData) => {
    updateScheduleMutation.mutate(data)
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to remove this schedule? The test will only run manually.')) {
      deleteScheduleMutation.mutate()
    }
  }

  if (!isEditing) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <CardTitle className="text-base">Schedule</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {currentSchedule ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Badge variant="secondary">{currentSchedule.cron}</Badge>
                <span className="text-sm text-muted-foreground">
                  in {currentSchedule.timezone}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {getScheduleType(currentSchedule.cron) === 'interval' 
                  ? `Runs every ${getIntervalMinutes(currentSchedule.cron)} minutes`
                  : 'Custom cron schedule'
                }
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No schedule configured. Test runs manually only.
            </p>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4" />
          <CardTitle className="text-base">Edit Schedule</CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(false)}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Schedule Type</Label>
            <Select
              value={form.watch('scheduleType')}
              onValueChange={(value: 'manual' | 'interval' | 'cron') =>
                form.setValue('scheduleType', value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual (Run on demand)</SelectItem>
                <SelectItem value="interval">Interval (Every X minutes)</SelectItem>
                <SelectItem value="cron">Cron Expression (Advanced)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Interval Configuration */}
          {form.watch('scheduleType') === 'interval' && (
            <div className="space-y-2">
              <Label htmlFor="intervalMinutes">Interval (minutes)</Label>
              <Input
                id="intervalMinutes"
                type="number"
                min="1"
                placeholder="60"
                {...form.register('intervalMinutes', { valueAsNumber: true })}
              />
            </div>
          )}

          {/* Cron Configuration */}
          {form.watch('scheduleType') === 'cron' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="cronExpression">Cron Expression</Label>
                <Input
                  id="cronExpression"
                  placeholder="0 9 * * *"
                  {...form.register('cronExpression')}
                />
                {form.formState.errors.cronExpression && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.cronExpression.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select
                  value={form.watch('timezone')}
                  onValueChange={(value) => form.setValue('timezone', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <div className="flex space-x-2">
              <Button
                type="submit"
                disabled={updateScheduleMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Schedule
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
            </div>
            
            {currentSchedule && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deleteScheduleMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove Schedule
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
